import React, { useRef, useState, useEffect } from 'react';
import type { NumericFormatProps } from 'react-number-format';
import { NumericFormat } from 'react-number-format';
import useDebounce from '@forvalter/utils/useDebounce';
import type { TextFieldProps } from '@material-ui/core';
import { TextField, makeStyles } from '@material-ui/core';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => ({
    readOnlyField: {
        backgroundColor: theme.palette.primary.veryLight,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
        },
        '& .MuiOutlinedInput-input': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    editableField: {
        '& .MuiInputBase-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
}));

type FormikNumericFormat = {
    isReadOnly?: boolean;
    isRequired?: boolean;
    onFieldEditChange?: (value: number | undefined) => void;
} & NumericFormatProps<TextFieldProps>;

export const FormikNumericFormatEditable: React.FC<FormikNumericFormat> = ({
    id,
    label,
    isReadOnly,
    isRequired,
    placeholder,
    onFieldEditChange,
    value,
    ...rest
}) => {
    const classes = useStyles();
    const inputRef = useRef<HTMLInputElement>(null);
    const [localValue, setLocalValue] = useState(value);

    // Use debounced value for callback updates
    const { debouncedValue } = useDebounce(localValue, 300);

    const handleValueFormatting = (formattedValue?: string) => {
        if (inputRef && inputRef.current) {
            inputRef.current.value = formattedValue?.toString() ?? '';
        }
    };

    useEffect(() => {
        setLocalValue(value);
        // Ensure the underlying input DOM is formatted when external value changes
        handleValueFormatting((value as any)?.toString?.() ?? '');
    }, [value]);

    // Update callback when debounced value changes
    useEffect(() => {
        if (debouncedValue === value) return;
        onFieldEditChange?.(debouncedValue as number | undefined);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    return (
        <NumericFormat
            inputRef={inputRef}
            thousandSeparator={' '}
            decimalSeparator=","
            value={localValue ?? ''}
            className={classNames({
                [classes.editableField]: !isReadOnly,
                [classes.readOnlyField]: isReadOnly,
            })}
            id={id}
            customInput={TextField}
            label={label}
            placeholder={placeholder}
            type="text"
            fullWidth={true}
            multiline={true}
            required={isRequired}
            onValueChange={({ floatValue }) => {
                setLocalValue(floatValue);
                handleValueFormatting(floatValue?.toString());
                // Local state updates immediately, callback updates are debounced
            }}
            inputProps={{
                'aria-readonly': isReadOnly,
            }}
            disabled={isReadOnly}
            InputLabelProps={{
                shrink: true,
            }}
            {...rest}
        />
    );
};
